import { useQuery } from "@apollo/client";
import { gql } from "@/gql";
import { ShiftFilterInput } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";

// =============================================
// TEMPLATE QUERIES (Layer 1)
// =============================================

const GET_SHIFT_TEMPLATES = gql(`
  query GetShiftTemplates($franchiseId: ID!) {
    getShiftTemplates(franchiseId: $franchiseId) {
      id
      name
      description
      position
      scheduleType
      days
      color
      icon
      sortOrder
      isActive
      franchiseId
      createdAt
      updatedAt
      timeIntervals {
        id
        startTime
        endTime
        sortOrder
        templateId
      }
      breakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        templateId
      }
    }
  }
`);

const GET_SHIFT_TEMPLATE = gql(`
  query GetShiftTemplate($id: ID!) {
    getShiftTemplate(id: $id) {
      id
      name
      description
      position
      scheduleType
      days
      color
      icon
      sortOrder
      isActive
      franchiseId
      createdAt
      updatedAt
      timeIntervals {
        id
        startTime
        endTime
        sortOrder
        templateId
      }
      breakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        templateId
      }
    }
  }
`);

// =============================================
// PLAN QUERIES (Layer 2)
// =============================================

const GET_WORKER_PLAN = gql(`
  query GetWorkerPlan($workerSalonId: ID!) {
    getWorkerPlan(workerSalonId: $workerSalonId) {
      id
      workerSalonId
      planType
      templateId
      customScheduleType
      customDays
      isActive
      createdAt
      updatedAt
      template {
        id
        name
        description
        position
        scheduleType
        days
        color
        icon
        sortOrder
        isActive
        timeIntervals {
          id
          startTime
          endTime
          sortOrder
          templateId
        }
        breakRules {
          id
          afterHours
          breakType
          duration
          isPaid
          isRequired
          sortOrder
          templateId
        }
      }
      customTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        planId
      }
      customBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        planId
      }
    }
  }
`);

const GET_PLANS_FOR_TEMPLATE = gql(`
  query GetPlansForTemplate($templateId: ID!) {
    getPlansForTemplate(templateId: $templateId) {
      id
      workerSalonId
      planType
      templateId
      customScheduleType
      customDays
      isActive
      createdAt
      updatedAt
      customTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        planId
      }
      customBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        planId
      }
    }
  }
`);

// =============================================
// PATTERN QUERIES (Layer 3)
// =============================================

const GET_SHIFT_PATTERN = gql(`
  query GetShiftPattern($id: ID!) {
    getShiftPattern(id: $id) {
      id
      planId
      franchiseId
      sourceType
      managedBy
      resolvedDays
      resolvedScheduleType
      rotationData
      seasonalRules
      exceptionDates
      patternType
      startDate
      endDate
      nextGeneration
      lastGenerated
      isActive
      generateAhead
      createdAt
      updatedAt
      resolvedTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        patternId
      }
      resolvedBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        patternId
      }
    }
  }
`);

const GET_SHIFT_PATTERNS = gql(`
  query GetShiftPatterns($franchiseId: ID!) {
    getShiftPatterns(franchiseId: $franchiseId) {
      id
      planId
      franchiseId
      sourceType
      managedBy
      resolvedDays
      resolvedScheduleType
      rotationData
      seasonalRules
      exceptionDates
      patternType
      startDate
      endDate
      nextGeneration
      lastGenerated
      isActive
      generateAhead
      createdAt
      updatedAt
      resolvedTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        patternId
      }
      resolvedBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        patternId
      }
    }
  }
`);

// =============================================
// SHIFT QUERIES (Layer 4)
// =============================================

const GET_SHIFTS = gql(`
  query GetShifts($filters: ShiftFilterInput!) {
    getShifts(filters: $filters) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

const GET_SHIFT_HISTORY = gql(`
  query GetShiftHistory($shiftId: ID!) {
    getShiftHistory(shiftId: $shiftId) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

const FIND_REPLACEMENTS = gql(`
  query FindReplacements($shiftId: ID!) {
    findReplacements(shiftId: $shiftId) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

// =============================================
// TEMPLATE HOOKS (Layer 1)
// =============================================

interface UseShiftTemplatesQueryOptions {
  skip?: boolean;
}

export const useShiftTemplatesQuery = ({ skip = false }: UseShiftTemplatesQueryOptions = {}) => {
  const currentFranchise = useSalonStore((state) => state.getCurrentFranchise());

  const { data, loading, error, refetch } = useQuery(GET_SHIFT_TEMPLATES, {
    variables: {
      franchiseId: currentFranchise?.id || "",
    },
    skip: skip || !currentFranchise?.id,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const templates = data?.getShiftTemplates || [];

  return {
    templates,
    loading,
    error,
    refetch,
  };
};

interface UseShiftTemplateQueryOptions {
  templateId: string;
  skip?: boolean;
}

export const useShiftTemplateQuery = ({
  templateId,
  skip = false,
}: UseShiftTemplateQueryOptions) => {
  const { data, loading, error, refetch } = useQuery(GET_SHIFT_TEMPLATE, {
    variables: { id: templateId },
    skip: skip || !templateId,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const template = data?.getShiftTemplate;

  return {
    template,
    loading,
    error,
    refetch,
  };
};

// =============================================
// PLAN HOOKS (Layer 2)
// =============================================

interface UseWorkerPlanQueryOptions {
  workerSalonId: string;
  skip?: boolean;
}

export const useWorkerPlanQuery = ({ workerSalonId, skip = false }: UseWorkerPlanQueryOptions) => {
  const { data, loading, error, refetch } = useQuery(GET_WORKER_PLAN, {
    variables: { workerSalonId },
    skip: skip || !workerSalonId,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const plan = data?.getWorkerPlan;

  return {
    plan,
    loading,
    error,
    refetch,
  };
};

interface UsePlansForTemplateQueryOptions {
  templateId: string;
  skip?: boolean;
}

export const usePlansForTemplateQuery = ({
  templateId,
  skip = false,
}: UsePlansForTemplateQueryOptions) => {
  const { data, loading, error, refetch } = useQuery(GET_PLANS_FOR_TEMPLATE, {
    variables: { templateId },
    skip: skip || !templateId,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const plans = data?.getPlansForTemplate || [];

  return {
    plans,
    loading,
    error,
    refetch,
  };
};

// =============================================
// PATTERN HOOKS (Layer 3)
// =============================================

interface UseShiftPatternQueryOptions {
  patternId: string;
  skip?: boolean;
}

export const useShiftPatternQuery = ({ patternId, skip = false }: UseShiftPatternQueryOptions) => {
  const { data, loading, error, refetch } = useQuery(GET_SHIFT_PATTERN, {
    variables: { id: patternId },
    skip: skip || !patternId,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const pattern = data?.getShiftPattern;

  return {
    pattern,
    loading,
    error,
    refetch,
  };
};

interface UseShiftPatternsQueryOptions {
  skip?: boolean;
}

export const useShiftPatternsQuery = ({ skip = false }: UseShiftPatternsQueryOptions = {}) => {
  const currentFranchise = useSalonStore((state) => state.getCurrentFranchise());

  const { data, loading, error, refetch } = useQuery(GET_SHIFT_PATTERNS, {
    variables: {
      franchiseId: currentFranchise?.id || "",
    },
    skip: skip || !currentFranchise?.id,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const patterns = data?.getShiftPatterns || [];

  return {
    patterns,
    loading,
    error,
    refetch,
  };
};

// =============================================
// SHIFT HOOKS (Layer 4)
// =============================================

interface UseShiftsQueryOptions {
  filters: ShiftFilterInput;
  skip?: boolean;
}

export const useShiftsQuery = ({ filters, skip = false }: UseShiftsQueryOptions) => {
  const { data, loading, error, refetch } = useQuery(GET_SHIFTS, {
    variables: { filters },
    skip,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const shifts = data?.getShifts || [];

  return {
    shifts,
    loading,
    error,
    refetch,
  };
};

interface UseShiftHistoryQueryOptions {
  shiftId: string;
  skip?: boolean;
}

export const useShiftHistoryQuery = ({ shiftId, skip = false }: UseShiftHistoryQueryOptions) => {
  const { data, loading, error, refetch } = useQuery(GET_SHIFT_HISTORY, {
    variables: { shiftId },
    skip: skip || !shiftId,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const history = data?.getShiftHistory || [];

  return {
    history,
    loading,
    error,
    refetch,
  };
};

interface UseFindReplacementsQueryOptions {
  shiftId: string;
  skip?: boolean;
}

export const useFindReplacementsQuery = ({
  shiftId,
  skip = false,
}: UseFindReplacementsQueryOptions) => {
  const { data, loading, error, refetch } = useQuery(FIND_REPLACEMENTS, {
    variables: { shiftId },
    skip: skip || !shiftId,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const replacements = data?.findReplacements || [];

  return {
    replacements,
    loading,
    error,
    refetch,
  };
};

// =============================================
// LEGACY COMPATIBILITY EXPORTS
// =============================================

// Re-export commonly used types
export type { ShiftFilterInput };
